import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KawaTechLogo } from '../branding/kawatech';

const PasswordResetPage = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!token) {
      setError('Token de recuperação ausente.');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }

    setLoading(true);
    try {
      await api.resetPassword(token, password);
      setMessage('Senha atualizada com sucesso. Faça login com a nova senha.');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Token inválido ou expirado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[41%_59%]">
      <section className="flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-[430px]">
          <div className="mb-9 text-center">
            <KawaTechLogo className="mx-auto h-9 w-auto" />
            <h1 className="mt-7 text-3xl font-extrabold text-secondary">Redefinir senha</h1>
            <p className="mt-3 text-base text-primary/75">
              Crie uma nova senha para voltar a acessar o KawaTenda.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="font-bold">Nova senha</Label>
              <div className="relative">
                <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-primary/55" />
                <Input
                  id="password"
                  type="password"
                  minLength={6}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  className="h-12 rounded-none border-0 bg-[#edf3ff] pl-12 pr-4 shadow-none focus-visible:ring-1 focus-visible:ring-primary"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-bold">Confirmar senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                minLength={6}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                className="h-12 rounded-none border-0 bg-[#edf3ff] px-4 shadow-none focus-visible:ring-1 focus-visible:ring-primary"
              />
            </div>

            {error && <div className="bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
            {message && <div className="bg-primary/10 p-3 text-sm text-primary">{message}</div>}

            <Button type="submit" className="h-12 w-full bg-primary text-base font-bold" disabled={loading}>
              {loading ? 'Atualizando...' : 'Atualizar senha'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm">
            <Link to="/login" className="font-bold text-primary hover:underline">
              Voltar para o login
            </Link>
          </p>
        </div>
      </section>

    <section className="hidden min-h-screen items-center bg-primary px-20 py-16 text-white lg:flex">
      <div className="mx-auto max-w-[620px]">
          <KawaTechLogo variant="negative" className="h-10 w-auto" />
          <h2 className="mt-10 text-5xl font-extrabold leading-tight">
            Acesso protegido por token temporário
          </h2>
          <p className="mt-5 text-lg leading-8">
            Depois da redefinição, use sua nova senha na tela de login.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PasswordResetPage;
